/**
 * CCCWidget — shared Crystal Clear Criteria display used on both
 * ProfilePage and Phase3. Both instances read from (and write to)
 * the same auth-context user.criteriaCards, so they stay in sync
 * automatically without any additional plumbing.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Pencil, Sparkles, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/lib/supabase/client';
import { DEFAULT_CRITERIA_CARDS, toDisplayCards } from '@/features/crystal-clear/CriteriaCardsDisplay';
import CCCEditDialog, { type CriteriaCardData } from '@/features/crystal-clear/CCCEditDialog';

const CCCWidget = () => {
  const { user, session, updateUser } = useAuth();
  const { isMember } = useUserRole();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [cccOutcome, setCccOutcome] = useState<'crystal_clear' | 'call_recommended' | null>(null);
  const [cccLoading, setCccLoading] = useState(true);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) { setCccLoading(false); return; }
    supabase
      .from('ccc_sessions')
      .select('outcome_type')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setCccOutcome((data as { outcome_type?: 'crystal_clear' | 'call_recommended' } | null)?.outcome_type ?? null);
        setCccLoading(false);
      });
  }, [session?.user?.id]);

  const handleSaveCriteria = (cards: CriteriaCardData[]) => {
    const serialized = cards.map(({ label, value }) => ({ label, value }));
    updateUser({ criteriaCards: serialized, criteriaSubmitted: true });
  };

  const criteriaCards = user?.criteriaCards ?? DEFAULT_CRITERIA_CARDS;
  const STANDARD_LABELS = new Set(['Strategy', 'Property Type', 'Location', 'Condition', 'Price Range', 'Profitability']);
  const displayCards = toDisplayCards(criteriaCards.filter(c => STANDARD_LABELS.has(c.label)));
  const strategyNotes = criteriaCards.find(c => c.label === 'Strategy Notes')?.value ?? '';

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b border-border">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-gold" />
          <h2 className="font-semibold text-foreground text-sm">My Crystal Clear Criteria</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="text-xs gap-1.5 h-8">
            <Pencil className="h-3 w-3" />
            Edit
          </Button>
          {isMember && !cccLoading && cccOutcome === 'call_recommended' && (
            <Button
              size="sm"
              onClick={() => navigate('/crystal-clear-criteria')}
              className="gradient-gold text-primary-foreground shadow-gold text-xs gap-1.5 h-8"
            >
              <Phone className="h-3 w-3" />
              View Call Summary
            </Button>
          )}
          {isMember && !cccLoading && cccOutcome === 'crystal_clear' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/crystal-clear-criteria')}
              className="text-xs gap-1.5 h-8"
            >
              <Sparkles className="h-3 w-3" />
              Update Criteria
            </Button>
          )}
        </div>
      </div>

      {/* Criteria grid — matches Profile layout exactly */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        {displayCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className={`flex flex-col gap-1.5 p-3 sm:p-4 overflow-hidden min-w-0
                ${i > 0 ? 'border-l border-border' : ''}
                ${i >= 2 ? 'border-t sm:border-t-0' : ''}
                ${i >= 3 ? 'sm:border-t' : ''}
                ${i >= 3 && i < 6 ? 'lg:border-t-0' : ''}`}
            >
              <Icon className="h-4 w-4 text-gold/80 shrink-0" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground leading-none">
                {card.label}
              </p>
              {card.value ? (
                <p className="font-semibold text-foreground text-sm leading-snug break-words">{card.value}</p>
              ) : (
                <p className="text-xs text-muted-foreground/60 italic">Not set</p>
              )}
              {card.label === 'Strategy' && strategyNotes && (
                <p className="text-xs text-muted-foreground leading-snug break-words mt-0.5 italic">{strategyNotes}</p>
              )}
            </div>
          );
        })}
      </div>

      <CCCEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        cards={toDisplayCards(criteriaCards)}
        onSave={handleSaveCriteria}
      />
    </div>
  );
};

export default CCCWidget;
